import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "redux";
import type { PreferencesTabEnum } from "@tvg/sh-lib-preferences/utils/types";
import {
  getAccountNumber,
  getAlertPrefs,
  getNotificationPreferencesToggle,
  getResidenceState,
  getSelectedTab
} from "@tvg/sh-lib-preferences/redux/selectors";
import { setSelectedTab } from "@tvg/sh-lib-preferences/redux/actions";
import PreferencesCenter from "@tvg/atomic-ui/_templates/PreferencesCenter";
import tvgConf from "@tvg/conf";
import Account from "./components/Account";
import Betting from "./components/Betting";
import Notifications from "./components/Notifications";
import ConfirmModal from "./components/ConfirmModal";
import EmailUnsubscribe from "./components/Unsubscribe";
import { onGenericGtmEvent } from "./utils/gtm/generic";
import onPageViewGTMEvent from "./utils/gtm/pageView";

interface Props {
  showUnsubscribe: boolean;
}

export const Preferences = ({ showUnsubscribe }: Props) => {
  const dispatch: Dispatch = useDispatch();

  const selectedTab = useSelector(getSelectedTab);
  const notificationPreferences = useSelector(getNotificationPreferencesToggle);
  const accountId = useSelector(getAccountNumber);
  const residenceState = useSelector(getResidenceState);

  const alertState = useSelector(getAlertPrefs);

  useEffect(() => {
    if (
      tvgConf().product === "tvg4" &&
      !!accountId &&
      !!residenceState &&
      !showUnsubscribe
    ) {
      onPageViewGTMEvent({
        accountId: +accountId,
        selectedTab: notificationPreferences ? "notifications" : "betting",
        residenceState
      });
    }
  }, [accountId, residenceState]);

  useEffect(() => {
    dispatch(
      setSelectedTab(notificationPreferences ? "NOTIFICATIONS" : "BETTING")
    );
  }, [notificationPreferences]);

  const selectTab = (tabSelected: PreferencesTabEnum) => {
    if (selectedTab === tabSelected) {
      return;
    }

    dispatch(setSelectedTab(tabSelected));
    onGenericGtmEvent({
      gaEventAction: "Tab Opened",
      gaEventLabel: tabSelected,
      module: selectedTab
    });
  };

  const notificationTab = {
    title: "Communications",
    isActive: selectedTab === "NOTIFICATIONS",
    onClick: () => selectTab("NOTIFICATIONS"),
    isTitleUppercase: false,
    qaLabel: "notifications-tab"
  };

  const bettingTab = {
    title: "Betting",
    isActive: selectedTab === "BETTING",
    onClick: () => selectTab("BETTING"),
    isTitleUppercase: false,
    qaLabel: "betting-tab"
  };

  const accountTab = {
    title: "Account",
    isActive: selectedTab === "ACCOUNT",
    onClick: () => selectTab("ACCOUNT"),
    isTitleUppercase: false,
    qaLabel: "account-tab"
  };

  const tabs = notificationPreferences
    ? [notificationTab, bettingTab, accountTab]
    : [bettingTab, accountTab];

  const selectedComponent = () => {
    switch (selectedTab) {
      case "NOTIFICATIONS":
        return <Notifications />;
      case "BETTING":
        return <Betting />;
      case "ACCOUNT":
        return <Account />;
      default:
        return notificationPreferences ? <Notifications /> : <Betting />;
    }
  };

  return showUnsubscribe ? (
    <EmailUnsubscribe />
  ) : (
    <>
      <PreferencesCenter tabs={tabs} hasAlert={alertState.show}>
        {selectedComponent()}
      </PreferencesCenter>
      <ConfirmModal />
    </>
  );
};

export default Preferences;
